<template>
  <v-container class="donations-container standard-content-container">
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <v-icon
              @click="backToMain"
              v-if="showAlternativeView"
              large
              color="mid-grey"
              class="mt-1"
              >{{ icons.mdiArrowLeft }}</v-icon
            >
            <router-link
              v-else
              :to="
                donationsView === 'RemainderOfEstateAssetDonation'
                  ? '/specific-bequests-remainder-of-estate'
                  : '/specific-donations'
              "
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1>
              Specific bequests -
              <span class="donation-type text-highlight">assets</span>
            </h1>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="7" v-if="!showAlternativeView">
        <div class="left-section-container">
          <p>
            Pay careful consideration when adding multiple beneficiaries to
            receive the same property. For instance - if you leave one property
            to more than one person, keep in mind that these people will need to
            be able to agree on how occupation, maintenance and financial
            management of this property will take place in the future.
            Disagreements in this regard often lead to feuds, therefore think
            carefully when you make a bequest like this.
          </p>
          <div class="donate-section">
            <h2>Choose which asset you would like to bequeath</h2>
            <div v-if="!hasChosenAsset" class="mt-5 mb-5">
              <AlternativeButton
                :title="'Choose Asset'"
                @gradientButtonClicked="showAssetDialog = true"
              />
            </div>
            <div v-else class="list-item">
              <v-btn
                class="list-item-btn"
                :ripple="false"
                block
                color="white"
                @click="chosenAsset = {}"
              >
                <div class="list-item-text d-flex">
                  <span class="list-item-name pl-0">{{
                    chosenAsset.displayName
                  }}</span>
                  <v-icon class="cancel-icon">{{
                    icons.mdiCloseCircle
                  }}</v-icon>
                </div>
              </v-btn>
            </div>
          </div>
          <div class="donate-section">
            <h2>Who would you like to give this asset to?</h2>
            <ChooseBeneficiary
              :with-percentages="true"
              :beneficiaries="chosenBeneficiaries"
              :limit-beneficiaries="limitBeneficiaries"
              @beneficiarySelected="beneficiarySelected"
              @beneficiaryRemoved="beneficiaryRemoved"
              :validate-form="validateForm"
              @beneficiariesValidated="beneficiariesValidated"
            />
            <span v-if="beneficiaryErrorMessage" class="error-text">{{
              beneficiaryErrorMessage
            }}</span>
          </div>

          <v-row class="mt-6">
            <v-col cols="12">
              <div class="button-container">
                <GradientButton
                  v-if="displayNextButton"
                  :title="'Next'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save"
                />
              </div>
            </v-col>
          </v-row>

          <ChooseAssetDialog
            v-model="showAssetDialog"
            :current-donation-asset-id="currentDonationAssetId"
            @dialogClosed="showAssetDialog = false"
            @assetChosen="assetChosen"
          />
        </div>
      </v-col>

      <v-col cols="12" md="7" v-if="showAlternativeView">
        <h2>
          If {{ beneficiaryToSelectAlternative.beneficiaryDisplayName }} passes
          on before you, how would you like the part you left to them to be
          distributed?
        </h2>
        <RadioSelect
          v-model="beneficiaryToSelectAlternative.alternativeDonation"
          :items="[
            { value: 'their_children', text: 'their children' },
            { value: 'a_specific_person', text: 'a specific person' },
            { value: 'a_specific_charity', text: 'a specific charity' },
            { value: 'a_specific_trust', text: 'a specific trust' },
            {
              value: 'i_am_not_ready_to_make_a_decision',
              text: 'I am not ready to make a decision',
            },
          ]"
          :group="'alternative_beneficiary'"
          @change="callClearSelection"
          :flex-column="true"
        />

        <ChooseAlternativeBeneficiary
          v-if="showAlternativeBeneficiarySelection"
          :beneficiaries="selectedAlternativeBeneficiaries"
          :beneficiaries-to-select-from="this.alternativeBeneficiaries"
          :alternative-type="this.alternativeType"
          :parent-beneficiary="this.beneficiaryToSelectAlternative"
          @beneficiarySelected="alternativeBeneficiarySelected"
          @beneficiaryRemoved="alternativeBeneficiaryRemoved"
          :validate-form="validateForm"
          @beneficiariesValidated="beneficiariesValidated"
        />

        <v-row class="mt-6">
          <v-col cols="12">
            <div class="button-container">
              <GradientButton
                :disabled="alternativeNextDisabled"
                :title="'Next'"
                :small="true"
                :callout="'right'"
                @gradientButtonClicked="submitAlternativeDonationInformation"
              />
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import ChooseAssetDialog from './ChooseAssetDialog.vue';
  import ChooseBeneficiary from './ChooseBeneficiary.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { specificDonationMixin } from '../../mixins/specific-donation-mixin';
  import ChooseAlternativeBeneficiary from './ChooseAlternativeBeneficiary.vue';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';

  export default {
    name: 'AssetDonation',
    components: {
      ChooseAssetDialog,
      ChooseBeneficiary,
      ErrorSnackbar,
      ChooseAlternativeBeneficiary,
    },
    mixins: [formHelperMixin, specificDonationMixin],
    props: {
      specificDonation: Object,
    },
    data() {
      return {
        showAssetDialog: false,
        chosenAsset: {},
        chosenBeneficiaries: [],
        validateForm: false,
        beneficiaryToSelectAlternative: {},
        showAlternativeView: false,
        showAlternativeBeneficiarySelection: false,
        alternativeType: '',
        alternativeBeneficiaries: [],
        testator: {},
        selectedAlternativeBeneficiary: {},
        clearSelection: 1,
      };
    },
    computed: {
      donationsView() {
        return this.$route.name;
      },
      assetDonationOrigin() {
        return this.donationsView === 'RemainderOfEstateAssetDonation'
          ? 'RemainderOfEstateBequest'
          : 'Bequest';
      },
      alternativeNextDisabled() {
        if (this.alternativeType === '') {
          return true;
        }

        if (
          this.alternativeType === 'children' ||
          this.alternativeType === 'not_ready'
        ) {
          return false;
        }

        return (
          this.selectedAlternativeBeneficiary?.beneficiaryId == undefined &&
          this.selectedAlternativeBeneficiary?.id == undefined
        );
      },
      limitBeneficiaries() {
        return (
          this.chosenAsset.type != 'Property' &&
          this.chosenAsset.type != 'Vehicle' &&
          this.chosenAsset.type != 'FinancialAsset'
        );
      },
      selectedAlternativeBeneficiaries() {
        if (
          this.selectedAlternativeBeneficiary.beneficiaryId ||
          this.selectedAlternativeBeneficiary.id
        ) {
          return [this.selectedAlternativeBeneficiary];
        } else {
          return [];
        }
      },
      hasChosenAsset() {
        return this.chosenAsset.id;
      },
      displayNextButton() {
        return (
          this.chosenAsset.id &&
          this.chosenBeneficiaries.length &&
          !this.beneficiaryErrorMessage
        );
      },
      isEditDonation() {
        return this.specificDonation;
      },
      currentDonationAssetId() {
        return this.specificDonation ? this.specificDonation.assetId : null;
      },
      beneficiaryErrorMessage() {
        if (
          this.chosenBeneficiaries.length > 1 &&
          this.chosenAsset.id &&
          this.limitBeneficiaries
        ) {
          return (
            'You can only donate this asset type to 1 beneficiary. Please remove ' +
            (this.chosenBeneficiaries.length - 1) +
            ' beneficiary(ies).'
          );
        } else {
          return null;
        }
      },
    },
    created() {
      if (this.isEditDonation) {
        this.assignExistingDonationDetails();
      }
    },
    watch: {
      'beneficiaryToSelectAlternative.alternativeDonation'(newVal, oldVal) {
        this.setAlternativeBeneficiaries(newVal);
      },
    },
    methods: {
      backToMain() {
        this.showAlternativeView = false;
        this.chosenBeneficiaries.forEach((chosenBeneficiary, index) => {
          chosenBeneficiary.has_edited = undefined;
        });
      },
      callClearSelection(value) {
        this.alternativeBeneficiaryRemoved();
      },
      setAlternativeBeneficiaries(value) {
        switch (value) {
          case 'their_children':
            this.alternativeType = 'children';
            this.showAlternativeBeneficiarySelection = false;
            return;
          case 'a_specific_person':
            this.setAlternativePeople();
            this.alternativeType = 'people';
            this.showAlternativeBeneficiarySelection = true;
            return;
          case 'a_specific_charity':
            this.setAlternativeCharities();
            this.alternativeType = 'charity';
            this.showAlternativeBeneficiarySelection = true;
            return;
          case 'a_specific_trust':
            this.setAlternativeTrusts();
            this.alternativeType = 'trust';
            this.showAlternativeBeneficiarySelection = true;
            return;
          case 'i_am_not_ready_to_make_a_decision':
            this.alternativeType = 'not_ready';
            this.showAlternativeBeneficiarySelection = false;
            return;
          default:
            this.alternativeType = '';
            this.showAlternativeBeneficiarySelection = false;
            return;
        }
      },
      setAlternativePeople() {
        if (!this.testator.people) {
          this.alternativeBeneficiaries = [];
        }
        const selectedBeneficiaryId =
          this.beneficiaryToSelectAlternative.beneficiaryId;
        const alternativeSelectedBeneficiaryId =
          this.selectedAlternativeBeneficiary.beneficiaryId ||
          this.selectedAlternativeBeneficiary.id;
        this.alternativeBeneficiaries = this.testator.people.map(p => {
          if (parseInt(alternativeSelectedBeneficiaryId) === parseInt(p.id)) {
            return { ...p, disabledMessage: 'Already selected' };
          } else if (parseInt(selectedBeneficiaryId) === parseInt(p.id)) {
            return { ...p, disabledMessage: 'Invalid selection' };
          } else {
            return p;
          }
        });
      },
      setAlternativeCharities() {
        if (!this.testator.charities) {
          this.alternativeBeneficiaries = [];
        }
        const selectedBeneficiaryId =
          this.beneficiaryToSelectAlternative.beneficiaryId;
        this.alternativeBeneficiaries = this.testator.charities.map(p => {
          if (selectedBeneficiaryId === p.id) {
            return { ...p, disabledMessage: 'Invalid selection' };
          } else {
            return p;
          }
        });
      },
      setAlternativeTrusts() {
        if (!this.testator.trusts) {
          this.alternativeBeneficiaries = [];
        }
        const selectedBeneficiaryId =
          this.beneficiaryToSelectAlternative.beneficiaryId;
        this.alternativeBeneficiaries = this.testator.trusts.map(p => {
          if (selectedBeneficiaryId === p.id) {
            return { ...p, disabledMessage: 'Invalid selection' };
          } else {
            return p;
          }
        });
      },
      assignExistingDonationDetails() {
        // using native JSON functions removes reactivity
        // so we can clone an object without mutating the original source
        const specificDonation = JSON.parse(
          JSON.stringify(this.specificDonation)
        );

        this.chosenAsset.id = specificDonation.assetId;
        this.chosenAsset.type = specificDonation.assetType;
        this.chosenAsset.displayName = specificDonation.assetDisplayName;

        this.chosenBeneficiaries = specificDonation.beneficiaries.map(
          beneficiary => {
            const obj = {
              id: beneficiary.id,
              beneficiaryType: beneficiary.beneficiaryType,
              beneficiaryId: beneficiary.beneficiaryId,
              beneficiaryDisplayName: beneficiary.beneficiaryDisplayName,
              percentage: beneficiary.percentage,
              alternativeBeneficiary: beneficiary.alternativeBeneficiary,
              alternativeDonation: beneficiary.alternativeDonation?.replaceAll(
                ' ',
                '_'
              ),
            };

            if (obj.alternativeBeneficiary && obj.alternativeBeneficiary.id) {
              if (obj.alternativeBeneficiary.registration_number) {
                obj.alternativeBeneficiary.beneficiaryDisplayName = `${obj.alternativeBeneficiary.name} ${obj.alternativeBeneficiary.registration_number}`;
              } else if (obj.alternativeBeneficiary.first_names) {
                obj.alternativeBeneficiary.beneficiaryDisplayName = `${obj.alternativeBeneficiary.first_names} ${obj.alternativeBeneficiary.last_name}`;
              } else {
                obj.alternativeBeneficiary.beneficiaryDisplayName = `${obj.alternativeBeneficiary.name}`;
              }
            }

            return obj;
          }
        );
      },
      submitAlternativeDonationInformation() {
        if (this.isEditDonation) {
          this.navigateAlternativeDonationsEdit();
        } else {
          this.navigateAlternativeDonations();
        }
        window.scrollTo(0, 0);
      },

      navigateAlternativeDonations() {
        if (this.beneficiaryToSelectAlternative) {
          const index = this.chosenBeneficiaries.findIndex(
            b =>
              b.beneficiaryId ==
              this.beneficiaryToSelectAlternative.beneficiaryId
          );

          if (index !== -1) {
            this.chosenBeneficiaries[index].alternativeDonation =
              this.beneficiaryToSelectAlternative.alternativeDonation;
          }
        }

        const beneficiaryWithoutAlternative = this.chosenBeneficiaries.filter(
          b => b.has_edited == undefined && b.beneficiaryType == 'Person'
        );

        if (beneficiaryWithoutAlternative.length) {
          if (
            beneficiaryWithoutAlternative[0]?.alternativeBeneficiary
              ?.beneficiaryId
          ) {
            this.selectedAlternativeBeneficiary =
              beneficiaryWithoutAlternative[0].alternativeBeneficiary;
          } else {
            this.selectedAlternativeBeneficiary = {};
          }

          beneficiaryWithoutAlternative[0].has_edited = true;
          this.beneficiaryToSelectAlternative =
            beneficiaryWithoutAlternative[0];
          this.showAlternativeView = true;
        } else {
          this.beneficiaryToSelectAlternative = {};
          this.showAlternativeView = false;
          this.selectedAlternativeBeneficiary = {};

          // Submit details
          this.createSpecificDonation(this.specificDonationInputDetails());
        }
      },
      navigateAlternativeDonationsEdit() {
        const index = this.chosenBeneficiaries.findIndex(
          b => b.has_edited == undefined && b.beneficiaryType == 'Person'
        );

        if (index !== -1) {
          this.selectedAlternativeBeneficiary =
            this.chosenBeneficiaries[index].alternativeBeneficiary;
          this.showAlternativeView = true;
          this.chosenBeneficiaries[index].has_edited = true;
          this.beneficiaryToSelectAlternative = this.chosenBeneficiaries[index];
          this.beneficiaryToSelectAlternative.alternativeDonation =
            this.chosenBeneficiaries[index].alternativeDonation;
          this.setAlternativeBeneficiaries(
            this.beneficiaryToSelectAlternative.alternativeDonation
          );
        } else {
          this.updateSpecificDonation(this.specificDonationInputDetails());
        }
      },

      assetChosen(asset) {
        this.chosenAsset = asset;
        this.showAssetDialog = false;
      },
      beneficiarySelected(beneficiary) {
        this.chosenBeneficiaries.push(beneficiary);
      },
      alternativeBeneficiarySelected(beneficiary) {
        this.chosenBeneficiaries.forEach((chosenBeneficiary, index) => {
          if (
            chosenBeneficiary.beneficiaryId === beneficiary.parentBeneficiaryId
          ) {
            chosenBeneficiary.alternativeBeneficiary = beneficiary;
          }
        });

        if (beneficiary.beneficiaryId) {
          this.selectedAlternativeBeneficiary = beneficiary;
          this.setAlternativeBeneficiaries(
            this.beneficiaryToSelectAlternative.alternativeDonation
          );
        }
      },
      alternativeBeneficiaryRemoved() {
        this.selectedAlternativeBeneficiary = {};
        this.beneficiaryToSelectAlternative.alternativeBeneficiary = {};
        this.setAlternativeBeneficiaries(
          this.beneficiaryToSelectAlternative.alternativeDonation
        );
      },
      beneficiaryRemoved(index) {
        this.chosenBeneficiaries.splice(index, 1);
      },
      save() {
        this.validateForm = !this.validateForm;
        window.scrollTo(0, 0);
      },
      beneficiariesValidated(valid) {
        if (valid) {
          if (this.isEditDonation) {
            this.navigateAlternativeDonationsEdit(
              this.specificDonationInputDetails()
            );
          } else {
            this.navigateAlternativeDonations(
              this.specificDonationInputDetails()
            );
          }
        }
      },
      specificDonationInputDetails() {
        const beneficiaries = this.chosenBeneficiaries.map(b => {
          return {
            id: b.id,
            beneficiaryId: b.beneficiaryId,
            beneficiaryType: b.beneficiaryType,
            percentage: b.percentage.toString(),
            alternativeDonation: b.alternativeDonation,
            alternativeBeneficiary: {
              id: b.alternativeBeneficiary?.beneficiaryId,
            },
          };
        });

        return {
          testatorId: this.testatorId,
          assetId: this.chosenAsset.id,
          assetType: this.chosenAsset.type,
          donationOrigin: this.assetDonationOrigin,
          beneficiariesAttributes: beneficiaries,
          donationType: 'ASSET',
        };
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  h2 {
    line-height: 28px;
  }
  .button-container {
    margin-bottom: 48px;
  }
  .donations-container {
    padding-top: 40px;
  }
  .list-item {
    max-width: 450px;
    margin: 30px 12px 30px 0;

    .list-item-text {
      display: flex;
      align-items: center;
      .cancel-icon {
        margin-left: auto;
      }
    }
  }

  .add-item-btn {
    width: 100%;
    max-width: 450px;
  }

  .donation-type {
    padding: 0 7px 4px 7px;
    border-radius: 7px;
    line-height: 1;
    display: inline-block;
  }

  .donation-type-assets-img {
    max-width: 280px;
  }

  .donate-section {
    margin-top: 30px;
  }
  .navigation-header {
    gap: 76px;
  }
</style>
